import React, { MouseEvent, FunctionComponent } from 'react';
import classNames from 'classnames';

//components
import { StyledButton, ButtonTypesType } from '../Common/Button/StyledButton';

// styles
import styles from './main.scss';

type ButtonProps = {
    className?: string;
    children?: React.ReactNode;
    dataTn: string;
    disabled?: boolean;
    fullWidth?: boolean;
    href?: string;
    isDangerousAction?: boolean;
    noFollow?: boolean;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    opensInNewWindow?: boolean;
    size?: 'small' | 'medium' | 'large';
    type?: 'primary' | 'primaryAlt' | 'secondary' | 'textCta' | 'transparent' | 'designManager';
    htmlType?: ButtonTypesType;
};

export const Button: FunctionComponent<ButtonProps> = props => {
    const {
        children,
        dataTn,
        className,
        disabled,
        fullWidth,
        href,
        isDangerousAction,
        noFollow,
        onClick = () => {},
        opensInNewWindow,
        size = 'large',
        type = 'primary',
        htmlType,
    } = props;

    if (
        !['primary', 'primaryAlt', 'secondary', 'textCta', 'transparent', 'designManager'].includes(
            type
        ) &&
        process.env.NODE_ENV !== 'production'
    ) {
        console.warn(
            `dibs-elements | Button | Type Error: You have passed in "${type}" for prop "type". Only 'primary', 'primaryAlt', 'secondary', 'textCta', 'designManager', or 'transparent' is allowed. Did you mean to use "htmlType"? 💖 Majd`
        );
    }
    const isPrimary = !['secondary', 'textCta', 'transparent', 'designManager'].includes(type);

    const outputClassName = classNames(
        {
            [styles.primaryAltButton]: type === 'primaryAlt',
            [styles.secondaryButton]: type === 'secondary',
            [styles.transparentButton]: type === 'transparent',
            [styles.textCtaButton]: type === 'textCta',
            [styles.designManagerButton]: type === 'designManager',
            [styles.isDangerousActionPrimary]: isDangerousAction && type === 'primary',
            [styles.isDangerousActionSecondary]:
                isDangerousAction && type !== 'primary' && type !== 'transparent',
            [styles.isDangerousActionTransparent]: isDangerousAction && type === 'transparent',
            [styles.fullWidth]: fullWidth,
            [styles.disabled]: disabled,
        },
        styles[size],
        className
    );
    const optionalProps: {
        disabled?: boolean;
        target?: string;
        rel?: string;
        type?: ButtonTypesType;
    } = {};
    if (disabled) {
        optionalProps.disabled = true;
    }
    if (typeof href === 'string') {
        if (opensInNewWindow) {
            optionalProps.target = '_blank';
            optionalProps.rel = 'noopener'; // https://developers.google.com/web/tools/lighthouse/audits/noopener
        }
        if (noFollow) {
            if (optionalProps.rel) {
                optionalProps.rel += ' nofollow';
            } else {
                optionalProps.rel = 'nofollow';
            }
        }
        return (
            <StyledButton
                className={outputClassName}
                dataTn={dataTn}
                href={href}
                isPrimary={isPrimary}
                onClick={onClick}
                {...optionalProps}
            >
                {children}
            </StyledButton>
        );
    }
    if (htmlType) {
        optionalProps.type = htmlType;
    }

    return (
        <StyledButton
            className={outputClassName}
            dataTn={dataTn}
            isPrimary={isPrimary}
            onClick={onClick}
            {...optionalProps}
        >
            {children}
        </StyledButton>
    );
};
